import { useNavigate } from "@tanstack/react-router";
import { useT } from "../i18n";
import { useSitzung } from "./Sitzung";
import { rechteAus } from "./rechte";
import {
  angemeldeteGruppen, anlegenSchliessen, useAnlegenOffen, useAnlegestand,
  type Anlegegriff, type Anlegegruppe,
} from "./anlegen";
import { Blattvonunten } from "../bausteine/Blattvonunten";
import { ortsaufnahmeOeffnen } from "./ortsaufnahme";
import { Zeichen } from "../bausteine/Zeichen";
import type { Recht } from "../daten/typen";
import "../stile/handy-leiste.css";

/* ══════════════════════════════════════════════════════════════════
   Das Blatt hinter dem Kreis (19.09.2026).

   Aufbau nach dem Vorbild (OVIS, `ngTelefonSuche.css`, Abschnitt 6: das
   Anlegeblatt am Telefon): ein Blatt von unten, je Griff eine Zeile mit
   Zeichen, Wort und leisem Satz darunter. **Oben steht, was die Maske
   anbietet, darunter, was immer geht** — das Vorbild trennt dieselben
   beiden Gruppen („bestehender Kunde" / „neu") mit einem Band.

   **Was immer geht, hängt am Recht und steht in der Reihenfolge von
   Regel 10:** Vermieten zuerst, das Angebot danach, der Kunde zuletzt.
   *„Der Regelfall hat kein Angebot."*

   **Ein Griff der Maske darf einen allgemeinen ersetzen** (`statt`). In der
   Kundenakte heißt „Vermieten" *„Vermieten für Kranbau"* und merkt sich
   den Kunden — ein zweites, blankes „Vermieten" darunter wäre derselbe Weg
   mit weniger Wissen, und man müsste raten, welcher der richtige ist.
   ══════════════════════════════════════════════════════════════════ */

/** Welche allgemeinen Griffe es gibt — der Schlüssel ist das, was `statt` nennt. */
export type Allgemein = "vermieten" | "angebot" | "aufmass" | "kunde";

const ALLGEMEIN: {
  art: Allgemein; recht: Recht; griff: Omit<Anlegegriff, "tun">;
  weg?: string; such?: Record<string, string>;
  /** Statt eines Wegs: ein Fenster, das auf der Seite aufgeht. */
  oeffnen?: () => void;
}[] = [
  {
    art: "vermieten", recht: "vermieten", weg: "/vermieten",
    griff: { wort: "anlegen.vermieten", satz: "anlegen.vermietenSatz", zeichen: "start" },
  },
  {
    art: "angebot", recht: "angebote", weg: "/angebote/neu",
    griff: { wort: "anlegen.angebot", satz: "anlegen.angebotSatz", zeichen: "angebot" },
  },
  /* **Das Aufmaß steht zwischen Angebot und Kunde** (21.09.2026, Migration
     0155). Luca: *„Wir haben ja NEU ANLEGEN Vermieten, Angebot, Kunde —
     mach dann Vermieten, Angebot, Aufmaß, Kunde."*

     Die Reihenfolge ist die von Regel 10, eine Stufe weiter gedacht: Was
     ein Geschäft **ist**, steht oben (Vermietung, Angebot); was eines
     **vorbereitet**, darunter (Aufmaß); wer eines **hat**, zuletzt (Kunde).

     **Seit dem 08.10.2026 ein Fenster auf der Seite, kein Sprung** (Paket
     ortsaufnahme). Luca: *„Wenn ich über ‚Neu' und ‚Aufmaß' gehe, muss
     direkt das Pop-up aufgehen. Dann brauche ich nicht extra dieses
     Ortsaufnahmen-Modul."* Bis dahin führte der Griff über `?neu=1` auf die
     Liste. Jetzt öffnet er das Fenster „Ortsaufnahme anlegen" dort, wo man
     gerade ist — am Telefon das Blatt von unten (`OrtsaufnahmeAnlegen.tsx`).
     Er heißt „Ortsaufnahme" wie die Sache überall sonst in der Anwendung. */
  {
    art: "aufmass", recht: "aufnahmen", oeffnen: () => ortsaufnahmeOeffnen(),
    griff: { wort: "anlegen.aufmass", satz: "anlegen.aufmassSatz", zeichen: "aufgaben" },
  },
  {
    art: "kunde", recht: "kunden", weg: "/kunden/neu",
    griff: { wort: "anlegen.kunde", satz: "anlegen.kundeSatz", zeichen: "kunden" },
  },
];

/**
 * Alle Gruppen, die das Blatt gerade zeigen würde — und ob sie von der Maske
 * kommen. Die Leiste fragt damit, ob der Kreis überhaupt steht, ob er gleich
 * springt und **welches Zeichen er trägt** (Plus oder Blitz).
 *
 * ── „Oder neu" gibt es im Vorgang nicht mehr (20.09.2026) ──
 *
 * Luca: *„Ich würde ‚Oder neu' und ‚Vermieten' bei ‚Kunde' in einem Auftrag
 * gar nicht als Möglichkeit anbieten, damit man da nicht rausgeht und zu viel
 * macht. … Dann machst du ‚Oder neu' komplett raus, weil die dann halt
 * zurückgehen und auf Plus gehen. Dann hast du das konsistent."*
 *
 * **Meldet eine Maske eigene Griffe an, stehen nur sie im Blatt.** Der Kreis
 * ist dort der Knopf **zu dieser Sache** — „Angebot senden", „Position
 * nachtragen", „Übernehmen" —, und wer daneben ein neues Angebot anfangen
 * könnte, fängt es aus Versehen an. Die allgemeinen Wege bleiben eine
 * Rückkehr entfernt: zurück auf Start oder eine Liste, dort das Plus.
 *
 * **`statt` bleibt deshalb ohne Wirkung, solange eigene Griffe stehen** — es
 * ersetzt einen allgemeinen Weg, den es in diesem Fall gar nicht gibt. In der
 * Kundenakte (`masken/kunden/Aktengriffe.tsx`) heißt das: „Vermieten für
 * Kranbau" und „Angebot für Kranbau" stehen weiter da, das blanke
 * „Vermieten"/„Angebot"/„Kunde" fällt weg — genau das war der Zweck von
 * `statt`, nur jetzt für die ganze Gruppe.
 */
export function useAnlegegruppen(): { gruppen: Anlegegruppe[]; eigen: boolean } {
  const t = useT();
  const gehZu = useNavigate();
  const { ich } = useSitzung();
  useAnlegestand();
  const hat = rechteAus(ich?.rollen ?? []);
  const eigene = angemeldeteGruppen();
  /* **Der Blitz gehört der Handlung, nicht dem Anlegen** (20.09.2026): Im
     Stamm meldet „Maschine anlegen" Griffe an, aber dort entsteht etwas
     Neues — das ist ein Plus. Eine Gruppe sagt es über `art`; ohne Angabe
     gilt „handlung", weil die Akten der häufige Fall sind. */
  if (eigene.length) return { gruppen: eigene, eigen: eigene.some((g) => g.art !== "neu") };
  const allgemein: Anlegegriff[] = ALLGEMEIN
    .filter((a) => hat.includes(a.recht))
    .map((a) => ({
      ...a.griff,
      tun: a.oeffnen ?? (() => void gehZu({ to: a.weg ?? "/", ...(a.such ? { search: a.such } : {}) })),
    }));
  return {
    gruppen: allgemein.length ? [{ titel: t("anlegen.neu"), griffe: allgemein }] : [],
    eigen: false,
  };
}

/** **„Neu" im Kopf des Schreibtischs** (07.10.2026, Signal, docs/64).
 *
 *  Dieselben Griffe wie hinter dem Kreis am Telefon — nur ohne die Handlungen
 *  einer Akte: Die stehen am Schreibtisch seit Paket 9 unter „Aktionen"
 *  daneben (`useAktionsgruppen`). Was eine Maske **anlegen** kann (`art: "neu"`, etwa „Maschine
 *  anlegen" im Stamm), steht oben, darunter das, was immer geht. */
export function useNeuGruppen(): Anlegegruppe[] {
  const t = useT();
  const gehZu = useNavigate();
  const { ich } = useSitzung();
  useAnlegestand();
  const hat = rechteAus(ich?.rollen ?? []);
  const eigene = angemeldeteGruppen().filter((g) => g.art === "neu");
  const allgemein: Anlegegriff[] = ALLGEMEIN
    .filter((a) => hat.includes(a.recht))
    .map((a) => ({
      ...a.griff,
      tun: a.oeffnen ?? (() => void gehZu({ to: a.weg ?? "/", ...(a.such ? { search: a.such } : {}) })),
    }));
  return [...eigene, ...(allgemein.length ? [{ titel: t("anlegen.neu"), griffe: allgemein }] : [])];
}

/** **„Aktionen" im Kopf des Schreibtischs** (07.10.2026, Paket 9, docs/64).
 *
 *  Zeigt, was die Seite an Handlungen anmeldet (`art` nicht `"neu"`) —
 *  dieselbe Anmeldung wie hinter dem Kreis am Telefon. **Ohne Handlungen
 *  der Seite gibt es den Knopf nicht** (Luca, 08.10.2026): Der Rückfall
 *  „Jetzt vermieten" ist weg — Vermieten steht unter „Neu". */
export function useAktionsgruppen(): Anlegegruppe[] {
  useAnlegestand();
  return angemeldeteGruppen().filter((g) => g.art !== "neu");
}

export function Anlegeblatt() {
  const t = useT();
  const auf = useAnlegenOffen();
  const { gruppen, eigen } = useAnlegegruppen();
  const waehlen = (g: Anlegegriff) => {
    anlegenSchliessen();
    g.tun();
  };
  return (
    /* Meldet die Maske eigene Griffe an — die Handlung der Phase, was sich an
       der Akte anlegen lässt —, ist das Blatt mehr als „Neu anlegen": oben
       steht dann etwa „Als Nächstes · Angebot senden" (19.09.2026). Der Titel
       sagt es neutral; die Gruppen darunter tragen ihre eigenen Namen. */
    <Blattvonunten
      offen={auf}
      schliessen={anlegenSchliessen}
      titel={t(eigen ? "anlegen.schildMaske" : "anlegen.schild")}
    >
      <div className="mehr-blatt">
        {gruppen.map((gruppe) => (
          <section className="mehr-gruppe" key={gruppe.titel}>
            {/* Eine Gruppe allein braucht keine Überschrift: „Neu" unter
                „Neu anlegen" sagt dasselbe zweimal. */}
            {gruppen.length > 1 ? <h4>{gruppe.titel}</h4> : null}
            <div className="hwege">
              {gruppe.griffe.map((g) => (
                /* **Eine Zeile, kein Satz darunter** (19.09.2026). Luca:
                   *„Egal, bei welcher Kachel ich bin, das muss immer einzeln
                   sein"* — „Maschine anlegen" mit „Kennung, Typ, Standort —
                   das Gerät steht im Brett" darunter, „Vermieten" mit „Gerät,
                   Kunde, Zeitraum — der Auftrag steht": Das Wort sagt, was
                   passiert; der Satz steht für Vorleser und Maus im `title`. */
                <button
                  key={g.wort}
                  type="button"
                  className="hweg-zeile"
                  title={g.gesperrt ? t(g.gesperrt) : g.satz ? t(g.satz) : undefined}
                  disabled={Boolean(g.gesperrt)}
                  onClick={() => waehlen(g)}
                >
                  <Zeichen name={g.zeichen} />
                  <b>{t(g.wort, g.werte)}</b>
                  <Zeichen name="weiter" />
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>
    </Blattvonunten>
  );
}
